import FormControlLabel from '@mui/material/FormControlLabel';

import { FilterListField } from 'common/components/FilterList';
import { RadioGroup } from 'common/components/FormField';
import { Radio } from 'common/components/Radio';
import {
    EFilterType,
    IFilterFormItem,
    TObjectFilterValue,
    TStringFilterValue,
} from 'common/types';
import { translate } from 'utils/i18n';
import { IBudgetRequestStatus } from 'graphql/types';
import {
    BUDGET_APPROVAL_ROLE,
    BUDGET_REQUEST_STATUS_CONFIG,
    BUDGET_REQUEST_STATUSES,
} from 'budget/constants/budgetApprovals';

export function budgetApprovalRoleFilter(
    transKey = 'budgetApprovalRoleFilter'
): IFilterFormItem<TStringFilterValue> {
    return {
        key: 'role',
        label: translate(`${transKey}.sectionTitle`),
        defaultExpanded: true,
        fields: [
            {
                name: 'role',
                type: EFilterType.Radio,
                initialValue: BUDGET_APPROVAL_ROLE.All,
                component: RadioGroup,
                componentProps: {
                    sx: { flexDirection: 'column', mt: -2 },
                    children: [
                        <FormControlLabel
                            control={<Radio color="primary" />}
                            key={BUDGET_APPROVAL_ROLE.All}
                            label={translate(`${transKey}.allOption`)}
                            value={BUDGET_APPROVAL_ROLE.All}
                        />,
                        <FormControlLabel
                            control={<Radio color="primary" />}
                            key={BUDGET_APPROVAL_ROLE.Manager}
                            label={translate(`${transKey}.managerOption`)}
                            value={BUDGET_APPROVAL_ROLE.Manager}
                        />,
                        <FormControlLabel
                            control={<Radio color="primary" />}
                            key={BUDGET_APPROVAL_ROLE.Hr}
                            label={translate(`${transKey}.hrOption`)}
                            value={BUDGET_APPROVAL_ROLE.Hr}
                        />,
                    ],
                },
                queryKey: 'status',
                chip: {
                    label: translate(`${transKey}.chip`),
                    parseValue: (value) =>
                        value === BUDGET_APPROVAL_ROLE.Manager
                            ? translate(`${transKey}.managerOption`)
                            : translate(`${transKey}.hrOption`),
                },
                // Maps the selected role to the `status` query variable. 'all'
                // returns undefined so the table applies its default statuses.
                parseQueryValue: (value) => {
                    if (value === BUDGET_APPROVAL_ROLE.Manager) {
                        return [IBudgetRequestStatus.PendingManager];
                    }

                    if (value === BUDGET_APPROVAL_ROLE.Hr) {
                        return [IBudgetRequestStatus.PendingHr];
                    }

                    return undefined;
                },
            },
        ],
    };
}

export function getBudgetApprovalFilters(): IFilterFormItem[] {
    return [budgetApprovalRoleFilter()] as IFilterFormItem[];
}

export function budgetRequestStatusFilter(
    transKey = 'userBudgetRequestStatusFilter'
): IFilterFormItem<TObjectFilterValue[]> {
    return {
        key: 'status',
        label: translate(`${transKey}.sectionTitle`),
        defaultExpanded: true,
        clearable: true,
        dense: true,
        fields: [
            {
                name: 'status',
                // Object type so the filter bar renders one labelled chip per
                // selected status and deletes it by matching its `id` — no chip
                // `parseValue` needed, which keeps the shared filter bar generic.
                type: EFilterType.Object,
                initialValue: [],
                component: FilterListField,
                componentProps: {
                    items: BUDGET_REQUEST_STATUSES.map((status) => ({
                        value: status,
                        label: translate(
                            BUDGET_REQUEST_STATUS_CONFIG[status].labelKey
                        ),
                    })),
                },
                queryKey: 'status',
                chip: {
                    label: translate(`${transKey}.chip`),
                },
                // Maps the selected statuses to the `status` query variable.
                // Selecting none or all is equivalent to no filter, so both
                // return undefined and every status is shown.
                parseQueryValue: (value) => {
                    if (
                        !Array.isArray(value) ||
                        !value.length ||
                        value.length === BUDGET_REQUEST_STATUSES.length
                    ) {
                        return undefined;
                    }

                    return value.map((item) => item.id);
                },
            },
        ],
    };
}

export function getUserBudgetRequestFilters(): IFilterFormItem[] {
    return [budgetRequestStatusFilter()] as IFilterFormItem[];
}
